import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service, SourceInfo } from 'webapp-types';
import { sanitize } from 'webapp/utils/html';
import { getVerifiedByName, getVerifiedDate, isVerified } from 'webapp/utils/verification';
import type { VerificationInfo } from 'webapp/utils/verification';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricAuditInfoComponent from 'webapp/components/metric/audit-info';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type CustomQuery from 'webapp/models-custom/custom-query';
import Metric from 'webapp/models-custom/metric';
import MetricFromClient from 'webapp/models-custom/metric-from-client';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type Script from 'webapp/models-custom/script';

export default class MetricLibraryPopupsPopupMetricBasicInfo extends PopupInnerContextComponent<PopupData.MetricLibrary.MetricBasicInfo> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;

	get model() {
		return this.popupData.model;
	}

	get isDatasetMetric(): boolean {
		return !!this.model.source?.isTypeOfDataset;
	}

	get isMetric(): boolean {
		return this.model instanceof Metric;
	}

	get isClientMetric(): boolean {
		return this.model instanceof MetricFromClient;
	}

	get name() {
		return this.model.name;
	}

	get descriptionHtml(): string {
		if (!this.isMetric && !this.isClientMetric) {
			return '';
		}

		const model = this.model as Metric;
		const customDescription = model.isTemporaryDisabledForMetricSelectPopupYoutube
			? 'Due to YouTube API Services Terms of Service, it is not allowed to create and calculate own derivated metrics from YouTube API Data.'
			: '';
		const description = sanitize(customDescription || model.description);
		let html = '';
		if (description) {
			html += `<br>${description}`;
		}
		if (this.currentUserService.isPowerUser) {
			html += `<br><br><span class="metric-data">key: ${model.key}<br>spaceAccessId: ${model.spaceAccessId}</span>`;
		}
		return html;
	}

	get sourceInfo(): SourceInfo | null {
		const model = this.model;
		return {
			avatar: model.sourceAvatar,
			label: model.sourceName || model.sourceType?.name || '',
		};
	}

	get verificationInfo(): VerificationInfo | undefined {
		if (!this.isMetric) {
			return undefined;
		}
		return (this.model as Metric).verificationInfo;
	}

	get isVerified(): boolean {
		return isVerified(this.verificationInfo);
	}

	get verifiedByName(): string {
		return getVerifiedByName(this.verificationInfo);
	}

	get verifiedDate(): string {
		return getVerifiedDate(this.verificationInfo);
	}

	get relatedModel(): CustomQuery | Script | null {
		if (!this.isMetric) {
			return null;
		}
		const metric = this.model as Metric;
		const objectId = metric.objectId;
		if (!objectId) {
			return null;
		}
		if (metric.isTypeCustomQuery) {
			return this.mmCustomQueriesService.findModel(String(objectId));
		}
		if (metric.isTypeMetricCalculation) {
			return this.mmScriptsService.findModel(String(objectId));
		}
		return null;
	}

	get auditCreatedByName(): string | null {
		return this.relatedModel?.createdBy?.name ?? null;
	}

	get auditCreatedTimestamp(): number | null {
		return this.relatedModel?.created ?? null;
	}

	get auditModifiedByName(): string | null {
		return this.relatedModel?.modifiedBy?.name ?? null;
	}

	get auditModifiedTimestamp(): number | null {
		return this.relatedModel?.model?.modified ?? null;
	}

	get editButtonText() {
		return `Edit ${this.model.isTypeGoal ? 'Goal' : 'Metric'}`;
	}

	@action
	openExtraInfoPopup() {
		this.mmMetricsService.openExtraInfoPopup(this.model as Metric);
	}

	@action
	editMetric() {
		if (this.isMetric) {
			(this.model as Metric).edit();
		}
	}

	@action
	openMetricInsights() {
		if (this.isMetric) {
			const metric = this.model as Metric;
			const metricSetup = new MetricSetup({
				key: metric.key,
				spaceAccessId: metric.spaceAccessId,
			});
			this.metricDetailsService.openMetricDetailsDrawer({ metricSetup });
		}
		this.close();
	}
	<template>
		<div>
			{{#if this.sourceInfo}}
				<div class="source">
					<img
						src={{this.sourceInfo.avatar}}
						width="16"
						height="16"
						alt={{this.sourceInfo.label}}
					/>
					<p class="source-title ellipsis-overflow">
						{{this.sourceInfo.label}}
						<ToolTipComponent @text={{this.sourceInfo.label}} />
					</p>
				</div>
			{{/if}}

			<div class="description">
				<strong>
					{{this.name}}
					{{#if this.isVerified}}
						<VerificationBadgeComponent
							@verification={{this.verificationInfo}}
							@size="sm"
							@showTooltip={{false}}
						/>
					{{/if}}
				</strong>
				{{{this.descriptionHtml}}}
			</div>

			<MetricAuditInfoComponent
				@verification={{this.verificationInfo}}
				@createdByName={{this.auditCreatedByName}}
				@createdTimestamp={{this.auditCreatedTimestamp}}
				@modifiedByName={{this.auditModifiedByName}}
				@modifiedTimestamp={{this.auditModifiedTimestamp}}
			/>

			{{#unless this.navigationService.isMobileApp}}
				{{#if this.isMetric}}
					{{#if this.model.canEdit}}
						<a role="button" class="edit" {{on "click" this.editMetric}}>
							<CommonUiIconComponent @name="Pencil" @size="sm" />
							{{this.editButtonText}}
						</a>
					{{else}}
						{{#unless this.isDatasetMetric}}
							<a
								role="button"
								class="extra-info"
								{{on "click" this.openExtraInfoPopup}}
							>
								<CommonUiIconComponent @name="Book" @size="sm" />
								More about this metric
							</a>
						{{/unless}}
					{{/if}}

					<div class="view-insights">
						<a class="button theme-blue" {{on "click" this.openMetricInsights}}>
							View metric insights
						</a>
					</div>
				{{/if}}

			{{/unless}}
		</div>
	</template>
}
